import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Lock, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRICING } from "@/data/categories";

const PRICE = PRICING.ai.find((a) => a.key === "yearly_reflection").price;

// Yearly Reflection: one purchase = one calendar year, chosen before payment.
export const YearlyActions = ({ meta, threshold, made, consentBox, consented, busy, onGenerate }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [year, setYear] = useState(params.get("year") || "");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const y = Number(year);
  const owned = meta.is_admin || (meta.yearly_years || []).includes(y);
  const created = made.find((g) => g.year === y);

  const seePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const r = await api.post("/keepsakes/preview", { type: "yearly_reflection", year: y, consent: true });
      setPreview(r.data.preview);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" && d.startsWith("not_enough_memories:") ? d.split(":").slice(3).join(":") : formatApiErrorDetail(d) || "We couldn't prepare your preview.");
    } finally {
      setPreviewing(false);
    }
  };

  const status = (yr) => (made.some((g) => g.year === yr) ? " · created" : (meta.yearly_years || []).includes(yr) ? " · owned" : "");

  return (
    <div data-testid="yearly-actions" className="space-y-3">
      <p className="text-sm text-[#4A4540]">Each Yearly Reflection covers <strong>one calendar year</strong>. Choose the year first — other years are bought separately.</p>
      <select
        data-testid="yearly-year-select"
        value={year}
        onChange={(e) => { setYear(e.target.value); setPreview(null); }}
        className="rounded-full border border-[#4A4540]/20 bg-white px-4 py-2 text-sm text-[#2C2A28] focus:outline-none focus:ring-1 focus:ring-[#B08D57]"
      >
        <option value="">Choose a year…</option>
        {meta.years.map((o) => (
          <option key={o.year} value={o.year} disabled={o.count < threshold}>
            {`${o.year} — ${o.count} ${o.count === 1 ? "memory" : "memories"}${o.count < threshold ? ` (need ${threshold})` : status(o.year)}`}
          </option>
        ))}
      </select>

      {year && created && !meta.is_admin && (
        <p data-testid="yearly-created" className="flex items-center gap-2 text-sm text-[#4A6B5D]">
          <CheckCircle size={18} weight="duotone" /> Your {year} reflection is saved above — reread or download it anytime, free.
        </p>
      )}

      {year && owned && (!created || meta.is_admin) && (
        <div className="space-y-3">
          {consentBox}
          <Button data-testid="yearly-generate-btn" disabled={busy || !consented} onClick={() => onGenerate(y)}
            className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
            {busy ? "Writing…" : `Create my ${year} reflection`}
          </Button>
          <p className="text-xs text-[#8A7F76]">Paid for {year}. If writing fails, simply try again — no extra charge.</p>
        </div>
      )}

      {year && !owned && (
        <div className="space-y-3">
          {consentBox}
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="yearly-preview-btn" disabled={previewing || !consented} onClick={seePreview} variant="outline"
              className="rounded-full border-[#B08D57] text-[#B08D57] hover:bg-[#B08D57] hover:text-white">
              <Sparkle size={15} weight="duotone" className="mr-1" /> {previewing ? "Reading…" : "See a free preview"}
            </Button>
            <Button data-testid="yearly-buy-btn" onClick={() => navigate(`/yearly-reflection/${year}`)}
              className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
              <Lock size={14} weight="fill" className="mr-1.5" /> Buy {year} · {PRICING.currency}{PRICE}
            </Button>
          </div>
          {preview && (
            <div data-testid="yearly-preview-result" className="rounded-xl border border-[#7EA193]/30 bg-[#EAF1EE] p-4">
              <p className="font-handwriting text-xl leading-snug text-[#2C2A28]">{preview}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
